import {
  BUSINESS_TOOL_SECTIONS,
  BUSINESS_TOOL_SUB_SECTIONS,
  DIMENSIONALITY_OPTIONS,
  VOLUME_IN_OPTIONS,
} from 'constants/businessTool';
import { t } from 'i18next';

import { CONSUMER_OPTIONS, PRODUCT_SERVICE_OPTIONS, UNITS_VALUES } from 'components/BusinessToolForm/constants/options';

import { getTableHeadersYearsInitialValues } from 'helpers/getTableHeadersYearsInitialValues';

export const prefix =
  `${BUSINESS_TOOL_SECTIONS.MARKET_ANALYSIS}.${BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.MARKET_OVERVIEW}` as const;

export const MARKET_TRENDS_FIELD_NAMES = {
  TREND_NAME: 'trendName',
  TREND_CHARACTERISTICS: 'trendCharacteristics',
} as const;

export interface IMarketTrendsForm {
  [MARKET_TRENDS_FIELD_NAMES.TREND_NAME]: string;
  [MARKET_TRENDS_FIELD_NAMES.TREND_CHARACTERISTICS]: string;
}

export const marketTrendsInitialValues: IMarketTrendsForm = {
  [MARKET_TRENDS_FIELD_NAMES.TREND_NAME]: '',
  [MARKET_TRENDS_FIELD_NAMES.TREND_CHARACTERISTICS]: '',
};

export const YEARS_COLUMN_NAMES = {
  Y1: 'y1',
  Y2: 'y2',
  Y3: 'y3',
  Y4: 'y4',
  Y5: 'y5',
  Y6: 'y6',
} as const;

export const REGION_PERIODS_NAMES = {
  REGION: 'region',
  ...YEARS_COLUMN_NAMES,
} as const;

export interface IRegionConsumptionRow {
  [REGION_PERIODS_NAMES.REGION]: string;
  [REGION_PERIODS_NAMES.Y1]: string;
  [REGION_PERIODS_NAMES.Y2]: string;
  [REGION_PERIODS_NAMES.Y3]: string;
  [REGION_PERIODS_NAMES.Y4]: string;
  [REGION_PERIODS_NAMES.Y5]: string;
  [REGION_PERIODS_NAMES.Y6]: string;
}

export const regionPeriodsInitialValues = {
  [REGION_PERIODS_NAMES.REGION]: '',
  [REGION_PERIODS_NAMES.Y1]: '',
  [REGION_PERIODS_NAMES.Y2]: '',
  [REGION_PERIODS_NAMES.Y3]: '',
  [REGION_PERIODS_NAMES.Y4]: '',
  [REGION_PERIODS_NAMES.Y5]: '',
  [REGION_PERIODS_NAMES.Y6]: '',
};

export const regionHeaderInitialValues = getTableHeadersYearsInitialValues(REGION_PERIODS_NAMES, -3);

export const FORECASTED_CONSUMPTION_CHART_NAMES = {
  TITLE: 'title',
  REGION: 'region',
  UNITS: 'units',
  TABLE_ROWS: 'volumePeriods',
  TABLE_HEADINGS: 'tableHeadings',
  SOURCE: 'source',
} as const;

type UnitValueKeys = keyof typeof UNITS_VALUES;

export type UnitValues = (typeof UNITS_VALUES)[UnitValueKeys];

export const UNITS_NAMES_STRUCTURE = {
  CURRENCY: 'currency',
  SELECTED_UNITS: 'selectedUnits',
  VOLUME: 'volume',
} as const;

export interface IForecastedConsumptionChart {
  [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: string;
  [FORECASTED_CONSUMPTION_CHART_NAMES.REGION]: string;
  [FORECASTED_CONSUMPTION_CHART_NAMES.UNITS]: {
    [UNITS_NAMES_STRUCTURE.SELECTED_UNITS]: UnitValues;
    [UNITS_NAMES_STRUCTURE.VOLUME]: string;
    [UNITS_NAMES_STRUCTURE.CURRENCY]: string;
  };
  [FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_HEADINGS]: IRegionConsumptionRow;
  [FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_ROWS]: IRegionConsumptionRow[];
  [FORECASTED_CONSUMPTION_CHART_NAMES.SOURCE]: string;
}

export const forecastedConsumptionChartInitialValues = {
  [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: t('chart'),
  [FORECASTED_CONSUMPTION_CHART_NAMES.REGION]: PRODUCT_SERVICE_OPTIONS[0].value,
  [FORECASTED_CONSUMPTION_CHART_NAMES.UNITS]: {
    [UNITS_NAMES_STRUCTURE.SELECTED_UNITS]: UNITS_VALUES.VOLUME_IN,
    [UNITS_NAMES_STRUCTURE.VOLUME]: VOLUME_IN_OPTIONS[0].value,
    [UNITS_NAMES_STRUCTURE.CURRENCY]: DIMENSIONALITY_OPTIONS[0].value,
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_HEADINGS]: regionHeaderInitialValues,
  [FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_ROWS]: [regionPeriodsInitialValues],
  [FORECASTED_CONSUMPTION_CHART_NAMES.SOURCE]: '',
};

export const CONSUMER_DETAILS_NAMES = {
  CONSUMER: 'consumer',
  PERCENTAGE_SHARE: 'percentageShare',
  MONETARY_SHARE: 'monetaryShare',
  PHYSICAL_SHARE: 'physicalShare',
} as const;

export interface IConsumerDetails {
  [CONSUMER_DETAILS_NAMES.CONSUMER]: string;
  [CONSUMER_DETAILS_NAMES.PERCENTAGE_SHARE]: string;
  [CONSUMER_DETAILS_NAMES.MONETARY_SHARE]: string;
  [CONSUMER_DETAILS_NAMES.PHYSICAL_SHARE]: string;
}

export const consumerDetailsInitialValues = {
  [CONSUMER_DETAILS_NAMES.CONSUMER]: '',
  [CONSUMER_DETAILS_NAMES.MONETARY_SHARE]: '',
  [CONSUMER_DETAILS_NAMES.PERCENTAGE_SHARE]: '',
  [CONSUMER_DETAILS_NAMES.PHYSICAL_SHARE]: '',
};

export const CURRENT_MARKET_STRUCTURE_NAMES = {
  TITLE: 'title',
  CONSUMER: 'consumer',
  DATE: 'date',
  UNITS: 'units',
  CONSUMER_DETAILS: 'consumerDetails',
  SOURCE: 'source',
} as const;

export interface ICurrentMarketStructure {
  [CURRENT_MARKET_STRUCTURE_NAMES.TITLE]: string;
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER]: string;
  [CURRENT_MARKET_STRUCTURE_NAMES.DATE]: string;
  [CURRENT_MARKET_STRUCTURE_NAMES.UNITS]: {
    [UNITS_NAMES_STRUCTURE.SELECTED_UNITS]: UnitValues;
    [UNITS_NAMES_STRUCTURE.VOLUME]: string;
    [UNITS_NAMES_STRUCTURE.CURRENCY]: string;
  };
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER_DETAILS]: IConsumerDetails[];
  [CURRENT_MARKET_STRUCTURE_NAMES.SOURCE]: string;
}

export const currentMarketStructureInitialValues = {
  [CURRENT_MARKET_STRUCTURE_NAMES.TITLE]: t('chart'),
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER]: CONSUMER_OPTIONS[2].value,
  [CURRENT_MARKET_STRUCTURE_NAMES.DATE]: '',
  [CURRENT_MARKET_STRUCTURE_NAMES.UNITS]: {
    [UNITS_NAMES_STRUCTURE.SELECTED_UNITS]: UNITS_VALUES.VOLUME_IN,
    [UNITS_NAMES_STRUCTURE.VOLUME]: VOLUME_IN_OPTIONS[0].value,
    [UNITS_NAMES_STRUCTURE.CURRENCY]: DIMENSIONALITY_OPTIONS[0].value,
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER_DETAILS]: [consumerDetailsInitialValues],
  [CURRENT_MARKET_STRUCTURE_NAMES.SOURCE]: '',
};

export const REGION_COLUMN_NAMES = {
  R1: 'r1',
  R2: 'r2',
  R3: 'r3',
  R4: 'r4',
} as const;

export const REGIONS_PRODUCT_NAMES = {
  PRODUCT: 'product',
  ...REGION_COLUMN_NAMES,
} as const;

export interface IAveragePricesRegionRow {
  [REGIONS_PRODUCT_NAMES.PRODUCT]: string;
  [REGIONS_PRODUCT_NAMES.R1]: string;
  [REGIONS_PRODUCT_NAMES.R2]: string;
  [REGIONS_PRODUCT_NAMES.R3]: string;
  [REGIONS_PRODUCT_NAMES.R4]: string;
}

export const regionProductsInitialValues = {
  [REGIONS_PRODUCT_NAMES.PRODUCT]: '',
  [REGIONS_PRODUCT_NAMES.R1]: '',
  [REGIONS_PRODUCT_NAMES.R2]: '',
  [REGIONS_PRODUCT_NAMES.R3]: '',
  [REGIONS_PRODUCT_NAMES.R4]: '',
};

export const regionsHeaderInitialValues = {
  [REGIONS_PRODUCT_NAMES.R1]: 'Region 1',
  [REGIONS_PRODUCT_NAMES.R2]: 'Region 2',
  [REGIONS_PRODUCT_NAMES.R3]: 'Region 3',
  [REGIONS_PRODUCT_NAMES.R4]: 'Region 4',
};

export const MARKET_ANALYSIS_OVERVIEW_NAMES = {
  MAIN_MARKET_OVERVIEW: 'mainMarketOverview',
  SERVICES_PRODUCTS_SUMMARY: 'servicesProductsSummary',
  CONSUMERS_DESCRIPTION: 'consumersDescription',
  MARKET_COMPETITORS: 'marketCompetitors',
  ACTUAL_AND_FORECASTED_CONSUMPTION: 'actualAndForecastedConsumption',

  FORECASTED_CONSUMPTION_CHART: 'forecastedConsumptionChart',
  FORECASTED_CONSUMPTIONS: 'forecastedConsumptions',
  FORECASTED_CONSUMPTIONS_DATA_SOURCE: 'forecastedConsumptionsDataSource',

  MARKET_SHARE: 'marketShare',

  MARKET_TRENDS: 'marketTrends',

  SALES_METHOD: 'salesMethod',
  MARKET_STRUCTURE_DESCRIPTION: 'marketStructureDescription',

  CURRENT_MARKET_STRUCTURE: 'currentMarketStructure',
  CURRENT_MARKET_STRUCTURE_DATA_SOURCE: 'currentMarketStructureDatASource',

  MARKET_GROWTH_FACTORS: 'marketGrowthFactors',
  TECHNOLOGY_IMPACT: 'technologyImpact',
  PRICE_STRATEGIES: 'priceStrategies',

  AVERAGE_PRICES_TITLE: 'averagePricesTitle',
  AVERAGE_PRICES_DATE: 'averagePricesDate',
  AVERAGE_PRICES_CURRENCY: 'averagePricesCurrency',

  AVERAGE_PRICES_CHART: 'averagePricesChart',
} as const;

export const AVERAGE_PRICES_CHART_NAMES = {
  AVERAGE_PRICES_REGIONS_ROWS: 'averagePricesRegionsRows',
  AVERAGE_PRICES_REGIONS_HEADINGS: 'averagePricesRegionsHeadings',
} as const;

export interface IAveragePricesChart {
  [AVERAGE_PRICES_CHART_NAMES.AVERAGE_PRICES_REGIONS_HEADINGS]: Omit<IAveragePricesRegionRow, 'product'>;
  [AVERAGE_PRICES_CHART_NAMES.AVERAGE_PRICES_REGIONS_ROWS]: IAveragePricesRegionRow[];
}

export const averagePricesChartInitialValues = {
  [AVERAGE_PRICES_CHART_NAMES.AVERAGE_PRICES_REGIONS_ROWS]: [regionProductsInitialValues],
  [AVERAGE_PRICES_CHART_NAMES.AVERAGE_PRICES_REGIONS_HEADINGS]: regionsHeaderInitialValues,
};

export interface IMarketAnalysisOverviewForm {
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.CONSUMERS_DESCRIPTION]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_COMPETITORS]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.FORECASTED_CONSUMPTION_CHART]: IForecastedConsumptionChart[];
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_SHARE]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS]: IMarketTrendsForm[];
  [MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.CURRENT_MARKET_STRUCTURE]: ICurrentMarketStructure[];
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.TECHNOLOGY_IMPACT]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_TITLE]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_DATE]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CURRENCY]: string;
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CHART]: IAveragePricesChart;
}

export const marketAnalysisOverviewInitialValues: IMarketAnalysisOverviewForm = {
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.CONSUMERS_DESCRIPTION]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_COMPETITORS]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.FORECASTED_CONSUMPTION_CHART]: [forecastedConsumptionChartInitialValues],
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_SHARE]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS]: [marketTrendsInitialValues],
  [MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.CURRENT_MARKET_STRUCTURE]: [currentMarketStructureInitialValues],
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.TECHNOLOGY_IMPACT]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_TITLE]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_DATE]: '',
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CURRENCY]: DIMENSIONALITY_OPTIONS[0].value,
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CHART]: averagePricesChartInitialValues,
};

export const MARKET_ANALYSIS_MAX_LENGTH = {
  AVERAGE_PRICES: {
    FIRST_COLUMN: 50,
  },
  CURRENT_MARKET_STRUCTURE: {
    DATA_SOURCE: 1000,
  },
  ACTUAL_AND_FORECASTED_CONSUMPTION: {
    CHART_TITLE: 150,
  },
};
